import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { adminApi, settingsApi } from '../../services/api';
import { useAuth } from '../../context/AuthContext';

/* ─── Design Tokens ─────────────────────────────────────────── */
const T = {
  bg:       '#f4f6f9',
  card:     '#ffffff',
  border:   '#e8ecf0',
  border2:  '#f0f3f6',
  text1:    '#0f172a',
  text2:    '#334155',
  text3:    '#64748b',
  text4:    '#94a3b8',
  accent:   '#c96428',
  accentL:  '#fff7f2',
  accentB:  '#fde0cc',
  green:    '#059669',  greenL: '#ecfdf5',
  blue:     '#2563eb',  blueL:  '#eff6ff',
  purple:   '#7c3aed',  purpleL:'#f5f3ff',
  amber:    '#d97706',  amberL: '#fffbeb',
  red:      '#dc2626',  redL:   '#fef2f2',
};

const STATUS = {
  pending:   { bg:'#fffbeb', color:'#92400e', dot:'#f59e0b' },
  confirmed: { bg:'#eff6ff', color:'#1e40af', dot:'#3b82f6' },
  preparing: { bg:'#fff7ed', color:'#9a3412', dot:'#f97316' },
  ready:     { bg:'#f0fdf4', color:'#065f46', dot:'#10b981' },
  delivered: { bg:'#f0fdf4', color:'#166534', dot:'#22c55e' },
  cancelled: { bg:'#fef2f2', color:'#991b1b', dot:'#ef4444' },
};

/* ─── Helpers ────────────────────────────────────────────────── */
const fmt = (n) => n >= 1000 ? `₹${(n/1000).toFixed(1)}k` : `₹${Math.round(n)}`;
const fmtN = (n) => n >= 1000 ? `${(n/1000).toFixed(1)}k` : String(n);

/* ─── Stat Card ──────────────────────────────────────────────── */
const StatCard = ({ label, value, sub, icon, color, colorL, trend, delay=0 }) => (
  <motion.div
    initial={{ opacity:0, y:16 }} animate={{ opacity:1, y:0 }} transition={{ delay, duration:0.35 }}
    style={{
      background: T.card, borderRadius:16, padding:'22px 24px',
      border:`1px solid ${T.border}`,
      boxShadow:'0 1px 3px rgba(0,0,0,0.04), 0 4px 16px rgba(0,0,0,0.04)',
      position:'relative', overflow:'hidden',
    }}>
    {/* Top accent line */}
    <div style={{ position:'absolute', top:0, left:0, right:0, height:3, background:`linear-gradient(90deg,${color},${color}88)`, borderRadius:'16px 16px 0 0' }}/>

    <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between' }}>
      <div style={{ flex:1 }}>
        <p style={{ fontSize:12, fontWeight:600, color:T.text3, textTransform:'uppercase', letterSpacing:'0.8px', margin:'0 0 12px' }}>{label}</p>
        <p style={{ fontSize:28, fontWeight:800, color:T.text1, margin:'0 0 6px', lineHeight:1, fontFamily:'"Inter",sans-serif' }}>{value}</p>
        <div style={{ display:'flex', alignItems:'center', gap:6 }}>
          {trend && (
            <span style={{ fontSize:11, fontWeight:600, color: parseFloat(trend) >= 0 ? T.green : T.red,
              background: parseFloat(trend) >= 0 ? T.greenL : T.redL,
              padding:'2px 7px', borderRadius:99 }}>
              {parseFloat(trend) >= 0 ? '↑' : '↓'} {Math.abs(parseFloat(trend))}%
            </span>
          )}
          {sub && <p style={{ fontSize:12, color:T.text4, margin:0 }}>{sub}</p>}
        </div>
      </div>
      <div style={{
        width:48, height:48, borderRadius:14,
        background: colorL, display:'flex', alignItems:'center', justifyContent:'center',
        fontSize:22, border:`1px solid ${color}22`, flexShrink:0,
      }}>{icon}</div>
    </div>
  </motion.div>
);

/* ─── Revenue Bar ────────────────────────────────────────────── */
const RevBar = ({ r, max, i, showLabel }) => {
  const val = parseFloat(r.revenue);
  const pct = max > 0 ? (val/max)*100 : 0;
  const isTop = val === max && val > 0;
  const d = new Date(r.date);
  return (
    <div style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center', gap:6, height:'100%', justifyContent:'flex-end' }}>
      {isTop && <span style={{ fontSize:10, fontWeight:700, color:T.accent, whiteSpace:'nowrap' }}>{fmt(val)}</span>}
      {!isTop && <span style={{ fontSize:10, color:'transparent' }}>0</span>}
      <motion.div
        initial={{ scaleY:0 }} animate={{ scaleY:1 }} transition={{ duration:0.5, delay:i*0.05 }}
        style={{
          width:'75%', originY:1, borderRadius:'6px 6px 3px 3px',
          height:`${Math.max(pct, val>0?5:0)}%`,
          background: isTop
            ? `linear-gradient(180deg,#f0874a,${T.accent})`
            : val > 0 ? `${T.accent}40` : T.border2,
          boxShadow: isTop ? `0 4px 12px ${T.accent}40` : 'none',
          minHeight: val > 0 ? 6 : 2,
        }}/>
      {showLabel && (
        <span style={{ fontSize:10, color: isTop ? T.accent : T.text4, fontWeight: isTop ? 700 : 400 }}>
          {d.toLocaleDateString('en-IN',{weekday:'short'})}
        </span>
      )}
    </div>
  );
};

/* ─── Main ───────────────────────────────────────────────────── */
const AdminDashboard = () => {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [gst,     setGst]     = useState({ enabled:false, rate:0, label:'GST' });
  const [tab,     setTab]     = useState('7d');
  const { admin } = useAuth();

  useEffect(() => {
    adminApi.getDashboard().then(r => setData(r.data.data)).catch(()=>{}).finally(()=>setLoading(false));
    settingsApi.get().then(r => {
      const d = r.data.data || {};
      setGst({ enabled:d.gst_enabled==='true', rate:parseFloat(d.gst_rate)||0, label:d.gst_label||'GST' });
    }).catch(()=>{});
  }, []);

  const chartData   = tab==='7d' ? (data?.revenueChart||[]) : (data?.monthlyChart||[]);
  const maxRev      = chartData.reduce((m,r)=>Math.max(m,parseFloat(r.revenue)),0) || 1;
  const totalRev    = chartData.reduce((s,r)=>s+parseFloat(r.revenue),0) || 0;
  const dailyRev    = data?.stats?.dailyRevenue ?? 0;
  const thisMonth   = parseFloat(data?.monthComparison?.this_month||0);
  const lastMonth   = parseFloat(data?.monthComparison?.last_month||0);
  const monthTrend  = lastMonth > 0 ? ((thisMonth-lastMonth)/lastMonth*100).toFixed(1) : null;
  const maxTopItem  = data?.topItems?.length > 0 ? Math.max(...data.topItems.map(i=>parseInt(i.total_qty))) : 1;

  const statCards = [
    { label:'Total Orders',    value: loading?'—': fmtN(data?.stats?.totalOrders??0),
      icon:'📦', color:T.blue,   colorL:T.blueL,   sub:'All time', delay:0 },
    { label:"Today's Revenue", value: loading?'—': fmt(dailyRev),
      icon:'💰', color:T.green,  colorL:T.greenL,  sub:'Excl. cancelled', delay:0.06 },
    { label:'This Month',      value: loading?'—': fmt(thisMonth),
      icon:'📈', color:T.purple, colorL:T.purpleL, trend:monthTrend, sub:`Last: ${fmt(lastMonth)}`, delay:0.12 },
    { label:'Pending Orders',  value: loading?'—': fmtN(data?.stats?.pendingOrders??0),
      icon:'⏳', color:T.amber,  colorL:T.amberL,  sub:'Need attention', delay:0.18 },
  ];

  const hours = Array.from({length:24},(_,i)=>{
    const f = data?.peakHours?.find(d=>parseInt(d.hour)===i);
    return { hour:i, count: f ? parseInt(f.count) : 0 };
  });
  const maxHour = Math.max(...hours.map(h=>h.count), 1);
  const peakH = data?.peakHours?.length > 0
    ? data.peakHours.reduce((a,b)=>parseInt(a.count)>parseInt(b.count)?a:b)
    : null;

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:24, fontFamily:'"Inter","Segoe UI",sans-serif' }}>

      {/* ── Page Header ── */}
      <motion.div initial={{opacity:0,y:-8}} animate={{opacity:1,y:0}}
        style={{ display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:12 }}>
        <div>
          <h1 style={{ fontSize:22, fontWeight:800, color:T.text1, margin:0, letterSpacing:'-0.3px' }}>
            Dashboard
          </h1>
          <p style={{ fontSize:13, color:T.text3, margin:'4px 0 0' }}>
            {new Date().toLocaleDateString('en-IN',{weekday:'long',day:'numeric',month:'long',year:'numeric'})}
          </p>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
          {gst.enabled && (
            <div style={{ display:'flex', alignItems:'center', gap:6, padding:'6px 12px', borderRadius:8, background:T.accentL, border:`1px solid ${T.accentB}` }}>
              <div style={{ width:6, height:6, borderRadius:'50%', background:T.accent }}/>
              <span style={{ fontSize:12, fontWeight:600, color:T.accent }}>{gst.label} {gst.rate}% Active</span>
            </div>
          )}
          <div style={{ padding:'6px 14px', borderRadius:8, background:T.card, border:`1px solid ${T.border}`, fontSize:12, color:T.text3, fontWeight:500 }}>
            👋 Welcome, <strong style={{color:T.text1}}>{admin?.name}</strong>
          </div>
        </div>
      </motion.div>

      {/* ── Stat Cards ── */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(220px,1fr))', gap:16 }}>
        {statCards.map(s => <StatCard key={s.label} {...s}/>)}
      </div>

      {/* ── Revenue Chart + Order Types ── */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 280px', gap:16 }}>

        {/* Revenue Chart */}
        <div style={{ background:T.card, borderRadius:16, padding:'24px', border:`1px solid ${T.border}`, boxShadow:'0 1px 3px rgba(0,0,0,0.04)' }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:24 }}>
            <div>
              <h3 style={{ fontSize:15, fontWeight:700, color:T.text1, margin:0 }}>Revenue Overview</h3>
              <p style={{ fontSize:13, color:T.text3, margin:'4px 0 0' }}>
                Total: <strong style={{color:T.accent}}>{fmt(totalRev)}</strong>
                {gst.enabled && gst.rate>0 && <span style={{color:T.text4,fontSize:12}}> (incl. {gst.label})</span>}
              </p>
            </div>
            {/* Tab Switch */}
            <div style={{ display:'flex', background:T.bg, borderRadius:10, padding:3, border:`1px solid ${T.border}` }}>
              {[['7d','7 Days'],['30d','30 Days']].map(([k,l])=>(
                <button key={k} onClick={()=>setTab(k)}
                  style={{
                    padding:'6px 16px', borderRadius:7, border:'none', cursor:'pointer',
                    fontFamily:'inherit', fontSize:12, fontWeight:600,
                    background: tab===k ? T.accent : 'transparent',
                    color: tab===k ? 'white' : T.text3,
                    transition:'all 0.15s',
                  }}>{l}</button>
              ))}
            </div>
          </div>

          <div style={{ height:160, display:'flex', gap: tab==='30d'?2:10, alignItems:'flex-end', padding:'0 4px' }}>
            {loading
              ? Array.from({length:7}).map((_,i)=>(
                  <div key={i} style={{ flex:1, display:'flex', alignItems:'flex-end', justifyContent:'center', height:'100%' }}>
                    <div style={{ width:'75%', height:`${20+Math.random()*60}%`, borderRadius:6, background:T.border2 }}/>
                  </div>
                ))
              : chartData.length > 0
                ? chartData.map((r,i)=>(
                    <RevBar key={r.date} r={r} max={maxRev} i={i} showLabel={tab==='7d'}/>
                  ))
                : <div style={{flex:1,display:'flex',alignItems:'center',justifyContent:'center',color:T.text4,fontSize:13}}>No data yet</div>
            }
          </div>

          {tab==='30d' && chartData.length>0 && (
            <div style={{ display:'flex', justifyContent:'space-between', marginTop:8 }}>
              <span style={{fontSize:10,color:T.text4}}>{new Date(chartData[0]?.date).toLocaleDateString('en-IN',{day:'numeric',month:'short'})}</span>
              <span style={{fontSize:10,color:T.text4}}>{new Date(chartData[chartData.length-1]?.date).toLocaleDateString('en-IN',{day:'numeric',month:'short'})}</span>
            </div>
          )}
        </div>

        {/* Order Type Breakdown */}
        <div style={{ background:T.card, borderRadius:16, padding:'24px', border:`1px solid ${T.border}`, boxShadow:'0 1px 3px rgba(0,0,0,0.04)' }}>
          <h3 style={{ fontSize:15, fontWeight:700, color:T.text1, margin:'0 0 4px' }}>Order Types</h3>
          <p style={{ fontSize:12, color:T.text3, margin:'0 0 20px' }}>Last 30 days</p>

          {loading ? (
            [1,2,3].map(i=><div key={i} style={{height:14,borderRadius:99,background:T.border2,marginBottom:16}}/>)
          ) : data?.orderTypes?.length > 0 ? (
            <div style={{ display:'flex', flexDirection:'column', gap:18 }}>
              {data.orderTypes.map(t=>{
                const total = data.orderTypes.reduce((s,x)=>s+parseInt(x.count),0);
                const pct = total > 0 ? Math.round(parseInt(t.count)/total*100) : 0;
                const icons = {'dine-in':'🍽️','takeaway':'🥡','delivery':'🛵'};
                const colors = {'dine-in':T.accent,'takeaway':T.blue,'delivery':T.green};
                const col = colors[t.order_type] || T.accent;
                return (
                  <div key={t.order_type}>
                    <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:7}}>
                      <div style={{display:'flex',alignItems:'center',gap:7}}>
                        <span style={{fontSize:15}}>{icons[t.order_type]||'📦'}</span>
                        <span style={{fontSize:13,fontWeight:600,color:T.text2,textTransform:'capitalize'}}>{t.order_type}</span>
                      </div>
                      <div style={{display:'flex',alignItems:'center',gap:6}}>
                        <span style={{fontSize:12,color:T.text3}}>{t.count}</span>
                        <span style={{fontSize:11,fontWeight:700,color:col,background:`${col}12`,padding:'2px 7px',borderRadius:99}}>{pct}%</span>
                      </div>
                    </div>
                    <div style={{height:7,borderRadius:99,background:T.border2,overflow:'hidden'}}>
                      <motion.div initial={{width:0}} animate={{width:`${pct}%`}} transition={{duration:0.7}}
                        style={{height:'100%',borderRadius:99,background:`linear-gradient(90deg,${col},${col}bb)`}}/>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : <p style={{color:T.text4,fontSize:13,textAlign:'center',paddingTop:24}}>No data yet</p>}
        </div>
      </div>

      {/* ── Top Items + Peak Hours ── */}
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16 }}>

        {/* Top Items */}
        <div style={{ background:T.card, borderRadius:16, padding:'24px', border:`1px solid ${T.border}`, boxShadow:'0 1px 3px rgba(0,0,0,0.04)' }}>
          <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:20}}>
            <div style={{width:34,height:34,borderRadius:10,background:T.amberL,display:'flex',alignItems:'center',justifyContent:'center',fontSize:17}}>🏆</div>
            <div>
              <h3 style={{fontSize:15,fontWeight:700,color:T.text1,margin:0}}>Top Selling Items</h3>
              <p style={{fontSize:12,color:T.text3,margin:0}}>Last 30 days by quantity</p>
            </div>
          </div>

          {loading ? (
            [1,2,3,4,5].map(i=><div key={i} style={{height:40,borderRadius:10,background:T.border2,marginBottom:10}}/>)
          ) : data?.topItems?.length > 0 ? (
            <div style={{display:'flex',flexDirection:'column',gap:0}}>
              {data.topItems.map((item,rank)=>{
                const qty = parseInt(item.total_qty);
                const pct = maxTopItem > 0 ? (qty/maxTopItem)*100 : 0;
                const medals = ['🥇','🥈','🥉','4','5'];
                const barColors = [T.accent, T.blue, T.purple, T.green, T.amber];
                const col = barColors[rank];
                return (
                  <div key={item.item_name} style={{padding:'11px 0',borderBottom:rank<4?`1px solid ${T.border2}`:'none'}}>
                    <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:7}}>
                      <span style={{fontSize:rank<3?16:12,minWidth:22,textAlign:'center',fontWeight:rank>=3?700:400,color:rank>=3?T.text4:'inherit'}}>
                        {medals[rank]}
                      </span>
                      <span style={{flex:1,fontSize:13,fontWeight:600,color:T.text1}}>{item.item_name}</span>
                      <div style={{textAlign:'right'}}>
                        <span style={{fontSize:12,fontWeight:700,color:col}}>{qty} sold</span>
                        <span style={{fontSize:11,color:T.text4,marginLeft:6}}>₹{parseFloat(item.total_revenue).toFixed(0)}</span>
                      </div>
                    </div>
                    <div style={{height:5,borderRadius:99,background:T.border2,overflow:'hidden',marginLeft:32}}>
                      <motion.div initial={{width:0}} animate={{width:`${pct}%`}} transition={{duration:0.6,delay:rank*0.08}}
                        style={{height:'100%',borderRadius:99,background:`linear-gradient(90deg,${col},${col}99)`}}/>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : <p style={{color:T.text4,fontSize:13,textAlign:'center',paddingTop:24}}>No orders yet</p>}
        </div>

        {/* Peak Hours */}
        <div style={{ background:T.card, borderRadius:16, padding:'24px', border:`1px solid ${T.border}`, boxShadow:'0 1px 3px rgba(0,0,0,0.04)' }}>
          <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:20}}>
            <div style={{width:34,height:34,borderRadius:10,background:'#fff7ed',display:'flex',alignItems:'center',justifyContent:'center',fontSize:17}}>⏰</div>
            <div>
              <h3 style={{fontSize:15,fontWeight:700,color:T.text1,margin:0}}>Peak Hours</h3>
              <p style={{fontSize:12,color:T.text3,margin:0}}>Busiest ordering times</p>
            </div>
          </div>

          {/* Heatmap */}
          <div style={{display:'flex',gap:2,alignItems:'flex-end',height:72,marginBottom:8}}>
            {hours.map(h=>{
              const pct = h.count > 0 ? (h.count/maxHour)*100 : 0;
              const isPeak = peakH && parseInt(peakH.hour)===h.hour && h.count>0;
              return (
                <div key={h.hour} title={`${h.hour}:00 — ${h.count} orders`}
                  style={{flex:1,display:'flex',flexDirection:'column',alignItems:'center',height:'100%',justifyContent:'flex-end',cursor:'default'}}>
                  <motion.div
                    initial={{scaleY:0}} animate={{scaleY:1}} transition={{duration:0.4,delay:h.hour*0.015}}
                    style={{
                      width:'100%', borderRadius:'3px 3px 1px 1px', originY:1,
                      height:`${Math.max(pct, h.count>0?8:2)}%`,
                      background: isPeak
                        ? `linear-gradient(180deg,#f97316,${T.accent})`
                        : h.count>0 ? `${T.accent}50` : T.border2,
                      boxShadow: isPeak ? `0 3px 8px ${T.accent}40` : 'none',
                      minHeight: h.count>0 ? 4 : 2,
                    }}/>
                </div>
              );
            })}
          </div>

          {/* Hour labels */}
          <div style={{display:'flex',justifyContent:'space-between',marginBottom:16}}>
            {[0,6,12,18,23].map(h=>(
              <span key={h} style={{fontSize:10,color:T.text4}}>{h}:00</span>
            ))}
          </div>

          {/* Peak info */}
          {peakH ? (
            <div style={{
              padding:'12px 16px', borderRadius:12,
              background:`linear-gradient(135deg,${T.accentL},#fff)`,
              border:`1px solid ${T.accentB}`,
              display:'flex', alignItems:'center', gap:12,
            }}>
              <span style={{fontSize:22}}>🔥</span>
              <div>
                <p style={{fontSize:13,fontWeight:700,color:T.text1,margin:0}}>
                  {parseInt(peakH.hour)}:00 – {parseInt(peakH.hour)+1}:00
                </p>
                <p style={{fontSize:12,color:T.text3,margin:'2px 0 0'}}>{peakH.count} orders — busiest hour</p>
              </div>
            </div>
          ) : (
            <p style={{color:T.text4,fontSize:13,textAlign:'center'}}>No data yet</p>
          )}
        </div>
      </div>

      {/* ── Recent Orders ── */}
      <div style={{ background:T.card, borderRadius:16, border:`1px solid ${T.border}`, boxShadow:'0 1px 3px rgba(0,0,0,0.04)', overflow:'hidden' }}>

        {/* Table Header */}
        <div style={{ padding:'18px 24px', borderBottom:`1px solid ${T.border}`, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <div style={{display:'flex',alignItems:'center',gap:10}}>
            <div style={{width:34,height:34,borderRadius:10,background:T.blueL,display:'flex',alignItems:'center',justifyContent:'center',fontSize:17}}>🕐</div>
            <div>
              <h3 style={{fontSize:15,fontWeight:700,color:T.text1,margin:0}}>Recent Orders</h3>
              <p style={{fontSize:12,color:T.text3,margin:0}}>Latest 5 transactions</p>
            </div>
          </div>
          <a href="/admin/orders" style={{fontSize:12,fontWeight:600,color:T.accent,textDecoration:'none',padding:'6px 14px',borderRadius:8,background:T.accentL,border:`1px solid ${T.accentB}`}}>
            View All →
          </a>
        </div>

        {/* Table */}
        {loading ? (
          <div style={{padding:24}}>
            {[1,2,3].map(i=><div key={i} style={{height:48,borderRadius:10,background:T.border2,marginBottom:10}}/>)}
          </div>
        ) : data?.recentOrders?.length > 0 ? (
          <>
            {/* Column headers */}
            <div style={{ display:'grid', gridTemplateColumns:'60px 1fr 120px 80px 100px 100px', padding:'10px 24px', background:T.bg, borderBottom:`1px solid ${T.border}` }}>
              {['Order','Customer','Date','Items','Amount','Status'].map(h=>(
                <span key={h} style={{fontSize:11,fontWeight:700,color:T.text4,textTransform:'uppercase',letterSpacing:'0.6px'}}>{h}</span>
              ))}
            </div>

            {data.recentOrders.map((order,i)=>{
              const sc = STATUS[order.status] || STATUS.pending;
              return (
                <motion.div key={order.id}
                  initial={{opacity:0,x:-8}} animate={{opacity:1,x:0}} transition={{delay:i*0.05}}
                  style={{
                    display:'grid', gridTemplateColumns:'60px 1fr 120px 80px 100px 100px',
                    padding:'14px 24px', alignItems:'center',
                    borderBottom:i<data.recentOrders.length-1?`1px solid ${T.border2}`:'none',
                    transition:'background 0.15s',
                  }}
                  onMouseEnter={e=>e.currentTarget.style.background=T.bg}
                  onMouseLeave={e=>e.currentTarget.style.background='transparent'}
                >
                  <span style={{fontFamily:'monospace',fontWeight:800,color:T.accent,fontSize:13}}>#{order.order_number}</span>
                  <div>
                    <p style={{fontWeight:600,color:T.text1,fontSize:13,margin:0,textTransform:'capitalize'}}>{order.customer_name}</p>
                  </div>
                  <span style={{fontSize:12,color:T.text3}}>
                    {new Date(order.created_at).toLocaleDateString('en-IN',{day:'2-digit',month:'short',hour:'2-digit',minute:'2-digit',hour12:true})}
                  </span>
                  <span style={{fontSize:12,color:T.text3}}>{order.item_count} item{order.item_count!==1?'s':''}</span>
                  <span style={{fontWeight:700,color:T.text1,fontSize:14}}>₹{parseFloat(order.total_amount).toFixed(0)}</span>
                  <span style={{
                    display:'inline-flex',alignItems:'center',gap:5,
                    fontSize:11,fontWeight:600,padding:'4px 10px',borderRadius:99,
                    background:sc.bg,color:sc.color,textTransform:'capitalize',width:'fit-content',
                  }}>
                    <span style={{width:5,height:5,borderRadius:'50%',background:sc.dot,flexShrink:0}}/>
                    {order.status}
                  </span>
                </motion.div>
              );
            })}
          </>
        ) : (
          <div style={{padding:'48px 24px',textAlign:'center'}}>
            <div style={{fontSize:40,marginBottom:12}}>📋</div>
            <p style={{color:T.text3,fontSize:14}}>No orders yet</p>
          </div>
        )}
      </div>

    </div>
  );
};

export default AdminDashboard;
